const DB_NAME = 'responder_messenger_e2ee';
const DB_VERSION = 1;
const DEVICE_ID_STORAGE_KEY = 'responder.messaging.e2ee.device_id';
const STORES = {
  deviceKeys: 'device_keys',
  conversationKeys: 'conversation_keys',
  failedDecryptions: 'failed_decryptions',
};

let dbPromise = null;

const randomUuid = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const openDatabase = () => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable in this environment'));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORES.deviceKeys)) {
        db.createObjectStore(STORES.deviceKeys, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.conversationKeys)) {
        db.createObjectStore(STORES.conversationKeys, { keyPath: 'conversationId' });
      }
      if (!db.objectStoreNames.contains(STORES.failedDecryptions)) {
        db.createObjectStore(STORES.failedDecryptions, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
  });
};

const getDb = () => {
  if (!dbPromise) {
    dbPromise = openDatabase();
  }
  return dbPromise;
};

const transaction = async (storeName, mode, operation) => {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const request = operation(store);

    tx.oncomplete = () => resolve(request?.result);
    tx.onerror = () =>
      reject(tx.error || new Error(`IndexedDB transaction failed for ${storeName}`));
    tx.onabort = () =>
      reject(tx.error || new Error(`IndexedDB transaction aborted for ${storeName}`));
  });
};

export const getOrCreateDeviceId = () => {
  if (typeof window === 'undefined') return randomUuid();
  const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;
  const deviceId = randomUuid();
  window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
};

export const getStoredDeviceId = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
};

export async function getDeviceIdentity() {
  const result = await transaction(STORES.deviceKeys, 'readonly', (store) => store.get('active'));
  return result || null;
}

export async function setDeviceIdentity(identity) {
  const payload = {
    id: 'active',
    deviceId: identity?.deviceId,
    publicKeyPem: identity?.publicKeyPem,
    privateKeyPem: identity?.privateKeyPem,
    createdAt: identity?.createdAt || new Date().toISOString(),
  };
  await transaction(STORES.deviceKeys, 'readwrite', (store) => store.put(payload));
  return payload;
}

export async function getConversationKey(conversationId) {
  if (conversationId == null) return null;
  const result = await transaction(STORES.conversationKeys, 'readonly', (store) =>
    store.get(`${conversationId}`),
  );
  return result || null;
}

export async function setConversationKey(conversationId, conversationKey, keyVersion = 1) {
  if (conversationId == null) return null;
  const payload = {
    conversationId: `${conversationId}`,
    conversationKey,
    keyVersion: Number(keyVersion) || 1,
    updatedAt: new Date().toISOString(),
  };
  await transaction(STORES.conversationKeys, 'readwrite', (store) => store.put(payload));
  return payload;
}

export async function removeConversationKey(conversationId) {
  if (conversationId == null) return;
  await transaction(STORES.conversationKeys, 'readwrite', (store) =>
    store.delete(`${conversationId}`),
  );
}

export async function clearConversationKeys() {
  await transaction(STORES.conversationKeys, 'readwrite', (store) => store.clear());
}

const failedDecryptionId = (conversationId, messageId) => `${conversationId}:${messageId}`;

export async function hasFailedDecryption(conversationId, messageId) {
  if (conversationId == null || messageId == null) return false;
  const id = failedDecryptionId(conversationId, messageId);
  const result = await transaction(STORES.failedDecryptions, 'readonly', (store) => store.get(id));
  return Boolean(result);
}

export async function markFailedDecryption(conversationId, messageId, reason = '') {
  if (conversationId == null || messageId == null) return;
  const payload = {
    id: failedDecryptionId(conversationId, messageId),
    conversationId: `${conversationId}`,
    messageId: `${messageId}`,
    reason: `${reason || ''}`.slice(0, 200),
    updatedAt: new Date().toISOString(),
  };
  await transaction(STORES.failedDecryptions, 'readwrite', (store) => store.put(payload));
}
